import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Loader2, Plus, Trash2, Phone, Clock, Calendar, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { PreferencesList } from '@/components/PreferencesList';
import { NewClientBadge, ReferencePhotos } from '@/components/BookingExtras';
import { toast } from 'sonner';
import {
  format, startOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, subWeeks,
  addMonths, subMonths, isToday, isSameMonth, isSameDay, getDay, eachDayOfInterval
} from 'date-fns';
import { it } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOURS = Array.from({ length: 11 }, (_, i) => i + 9);

const AdminAgendaSection = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('day');
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientResults, setShowClientResults] = useState(false);
  const [newBooking, setNewBooking] = useState({ client_name: '', client_phone: '', client_email: '', service_name: '', date: '', time: '', operator_name: '' });
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [operators, setOperators] = useState([]);
  const { getAuthHeaders } = useAuth();
  const currentHourRef = useRef(null);

  // Auto-scroll to current hour when day view loads
  useEffect(() => {
    if (viewMode === 'day' && isToday(currentDate) && currentHourRef.current) {
      setTimeout(() => {
        currentHourRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [viewMode, currentDate, loading]);

  useEffect(() => { fetchBookings(); fetchServices(); fetchClients(); fetchOperators(); }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) { console.error('Failed to fetch services:', error); }
  };

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/operators`, { headers: getAuthHeaders() });
      setOperators(response.data);
    } catch (error) { console.error('Failed to fetch operators:', error); }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (error) { console.error('Failed to fetch clients:', error); }
  };

  const filteredClients = clients.filter(c =>
    clientSearch.length >= 2 && (
      c.first_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
      c.last_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
      c.phone?.includes(clientSearch)
    )
  );

  const selectClient = (client) => {
    setSelectedClient(client);
    setClientSearch(`${client.first_name} ${client.last_name}`);
    setNewBooking({
      ...newBooking,
      client_name: `${client.first_name} ${client.last_name}`,
      client_phone: client.phone || '',
      client_email: client.email || '',
      client_id: client.id,
    });
    setShowClientResults(false);
  };

  const openNewBookingForSlot = (date, hour) => {
    setNewBooking({
      client_name: '', client_phone: '', client_email: '', service_name: '',
      date: format(date, 'yyyy-MM-dd'),
      time: `${hour.toString().padStart(2, '0')}:00`,
    });
    setClientSearch('');
    setSelectedClient(null);
    setNewBookingOpen(true);
  };

  const openEditBooking = (booking, e) => {
    if (e) e.stopPropagation();
    setEditBooking(booking);
    setEditForm({
      client_name: booking.client_name || '',
      client_phone: booking.client_phone || '',
      service_name: booking.service_name || '',
      date: booking.date || '',
      time: booking.time || '',
      status: booking.status || 'pending',
      operator_name: booking.operator_name || '',
      notes: booking.notes || '',
    });
  };

  const handleSaveEditBooking = async () => {
    if (!editBooking) return;
    try {
      await axios.put(`${API}/bookings/${editBooking.id}`, editForm, { headers: getAuthHeaders() });
      toast.success('Prenotazione aggiornata!');
      setEditBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { headers: getAuthHeaders() });
      setBookings(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const getBookingsForSlot = (date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    const timeStr30 = `${hour.toString().padStart(2, '0')}:30`;
    return bookings.filter(b =>
      b.date === dateStr &&
      (b.time === timeStr || b.time === timeStr30) &&
      (b.status === 'confirmed' || b.status === 'pending')
    );
  };

  const getBookingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings.filter(b => b.date === dateStr && (b.status === 'confirmed' || b.status === 'pending'));
  };

  const getBookingColor = (booking) => {
    if (booking.status === 'pending') return 'bg-yellow-500/30 border-yellow-500/50 text-yellow-200';
    // Use operator color if available
    if (booking.operator_name) {
      const op = operators.find(o => o.name === booking.operator_name);
      if (op?.color) return '';  // Will use inline style instead
    }
    return 'bg-accent/20 border-accent/40 text-accent';
  };

  const getOperatorStyle = (booking) => {
    if (booking.status === 'pending' || !booking.operator_name) return {};
    const op = operators.find(o => o.name === booking.operator_name);
    if (op?.color) {
      return { backgroundColor: `${op.color}20`, borderColor: `${op.color}60`, color: op.color };
    }
    return {};
  };

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    setCurrentDate(new Date());
    setCurrentMonth(new Date());
  };

  const handleClearDayBookings = async () => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const todayBookings = bookings.filter(b => b.date === todayStr && (b.status === 'confirmed' || b.status === 'pending'));

    if (todayBookings.length === 0) {
      toast.info('Nessun appuntamento da cancellare oggi');
      setClearDialogOpen(false);
      return;
    }

    try {
      await Promise.all(
        todayBookings.map(b =>
          axios.put(`${API}/bookings/${b.id}`, { status: 'cancelled' }, { headers: getAuthHeaders() })
        )
      );
      toast.success(`${todayBookings.length} appuntamenti cancellati!`);
      fetchBookings();
    } catch (error) {
      toast.error('Errore nella cancellazione');
    }
    setClearDialogOpen(false);
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    try {
      const bookingData = {
        client_name: newBooking.client_name,
        client_phone: newBooking.client_phone,
        client_email: newBooking.client_email || '',
        service_name: newBooking.service_name,
        date: newBooking.date,
        time: newBooking.time,
        operator_name: newBooking.operator_name || '',
      };
      if (selectedClient) {
        bookingData.client_id = selectedClient.id;
      }
      await axios.post(`${API}/admin/bookings`, bookingData, { headers: getAuthHeaders() });
      toast.success('Appuntamento creato!');
      setNewBookingOpen(false);
      setNewBooking({ client_name: '', client_phone: '', client_email: '', service_name: '', date: '', time: '', operator_name: '' });
      setClientSearch('');
      setSelectedClient(null);
      fetchBookings();
    } catch (error) {
      toast.error("Errore nella creazione dell'appuntamento");
    }
  };

  // All active bookings sorted for list view
  const allActiveBookings = bookings
    .filter(b => b.status === 'confirmed' || b.status === 'pending')
    .sort((a, b) => {
      const dateComp = a.date.localeCompare(b.date);
      return dateComp !== 0 ? dateComp : a.time.localeCompare(b.time);
    });

  // Month calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = (getDay(monthStart) + 6) % 7; // Monday = 0

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed').length;

  const viewModes = [
    { id: 'month', label: 'Mese' },
    { id: 'week', label: 'Settimana' },
    { id: 'day', label: 'Giorno' },
    { id: 'list', label: 'Lista' },
  ];

  return (
    <div className="space-y-5" data-testid="agenda-section">
      {/* Header with title + action buttons */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <h2 className="text-2xl sm:text-3xl font-bold">Agenda Appuntamenti</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setClearDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full px-5"
              data-testid="clear-bookings-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Pulisci Appuntamenti
            </Button>
            <Button
              onClick={() => {
                setNewBooking({ client_name: '', client_phone: '', client_email: '', service_name: '', date: '', time: '' });
                setClientSearch('');
                setSelectedClient(null);
                setNewBookingOpen(true);
              }}
              className="bg-foreground text-background hover:bg-foreground/90 font-semibold rounded-full px-5"
              data-testid="new-booking-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Appuntamento
            </Button>
          </div>
        </div>

        {/* Legend + Filter */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex gap-4 text-sm flex-wrap">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-accent/60" /> Confermati ({confirmedCount})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-yellow-500/60" /> In Attesa ({pendingCount})
            </span>
          </div>
          <Select value={operatorFilter} onValueChange={setOperatorFilter}>
            <SelectTrigger className="w-[180px]" data-testid="operator-filter">
              <SelectValue placeholder="Tutti gli operatori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli operatori</SelectItem>
              {operators.map(op => (
                <SelectItem key={op.id} value={op.name}>
                  <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: op.color || '#3b82f6' }} />
                    {op.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">
          {viewMode === 'month'
            ? format(currentMonth, 'MMMM yyyy', { locale: it })
            : viewMode === 'day'
            ? format(currentDate, 'EEEE dd MMMM yyyy', { locale: it })
            : viewMode === 'list'
            ? 'Tutti gli appuntamenti'
            : `${format(currentWeekStart, 'MMMM dd', { locale: it })} - ${format(addDays(currentWeekStart, 6), 'dd', { locale: it })}`
          }
        </h3>
        {viewMode !== 'list' && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="agenda-today-btn">Oggi</Button>
            <Button variant="outline" size="sm" onClick={() => {
              if (viewMode === 'month') setCurrentMonth(subMonths(currentMonth, 1));
              else if (viewMode === 'day') setCurrentDate(addDays(currentDate, -1));
              else setCurrentWeekStart(subWeeks(currentWeekStart, 1));
            }} data-testid="agenda-prev-btn">
              <ChevronLeft className="h-4 w-4" /> Indietro
            </Button>
            <Button variant="outline" size="sm" onClick={() => {
              if (viewMode === 'month') setCurrentMonth(addMonths(currentMonth, 1));
              else if (viewMode === 'day') setCurrentDate(addDays(currentDate, 1));
              else setCurrentWeekStart(addWeeks(currentWeekStart, 1));
            }} data-testid="agenda-next-btn">
              Avanti <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* View Mode Buttons */}
      <div className="flex gap-2">
        {viewModes.map((mode) => (
          <button
            key={mode.id}
            onClick={() => setViewMode(mode.id)}
            data-testid={`view-mode-${mode.id}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
              viewMode === mode.id
                ? 'bg-white text-black border-white'
                : 'bg-transparent text-foreground border-border hover:border-foreground/40'
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {/* ===== WEEK VIEW ===== */}
      {viewMode === 'week' && (
        <div className="overflow-x-auto" data-testid="agenda-week-view">
          <div className="min-w-[700px]">
            <div className="grid grid-cols-8 border-b border-border">
              <div className="p-2 text-xs text-muted-foreground font-medium" />
              {weekDays.map((day) => (
                <div key={day.toISOString()} className={`p-2 text-center border-l border-border ${isToday(day) ? 'bg-accent/10' : ''}`}>
                  <p className="text-xs text-muted-foreground">{format(day, 'EEE', { locale: it })}</p>
                  <p className={`text-sm font-bold ${isToday(day) ? 'text-accent' : ''}`}>{format(day, 'dd')}</p>
                </div>
              ))}
            </div>
            {HOURS.map((hour) => (
              <div key={hour} className="grid grid-cols-8 border-b border-border/50 min-h-[60px]">
                <div className="p-2 text-xs text-muted-foreground font-medium border-r border-border/50 flex items-start">
                  {hour.toString().padStart(2, '0')}:00
                </div>
                {weekDays.map((day) => {
                  const slotBookings = getBookingsForSlot(day, hour);
                  return (
                    <div
                      key={day.toISOString()}
                      className={`p-1 border-l border-border/30 cursor-pointer hover:bg-accent/10 transition-colors ${isToday(day) ? 'bg-accent/5' : ''}`}
                      onClick={() => { if (slotBookings.length === 0) openNewBookingForSlot(day, hour); }}
                      title={slotBookings.length === 0 ? 'Clicca per prenotare' : ''}
                    >
                      {slotBookings.map((booking) => {
                        const op = operators.find(o => o.name === booking.operator_name);
                        return (
                        <div key={booking.id}
                          className={`text-xs p-1.5 rounded border mb-1 cursor-pointer hover:opacity-80 flex items-center gap-1.5 ${getBookingColor(booking)}`}
                          style={getOperatorStyle(booking)}
                          title={`${booking.client_name} - ${booking.service_name}${booking.operator_name ? ` (${booking.operator_name})` : ''}`}
                          onClick={(e) => { e.stopPropagation(); openEditBooking(booking, e); }}
                          data-testid={`agenda-booking-${booking.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold leading-tight flex items-center gap-1 min-w-0"><NewClientBadge booking={booking} iconOnly /><span className="truncate">{booking.client_name}</span></p>
                            <p className="truncate opacity-80 text-[10px]">{booking.service_name} {booking.time}</p>
                          </div>
                          {booking.operator_name && (
                            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                              style={{ backgroundColor: op?.color || '#3b82f6' }}>
                              {booking.operator_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        );
                      })}
                      {slotBookings.length === 0 && (
                        <div className="h-full min-h-[40px] flex items-center justify-center opacity-0 hover:opacity-30 transition-opacity">
                          <Plus className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== DAY VIEW ===== */}
      {viewMode === 'day' && (
        <div data-testid="agenda-day-view">
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="p-3 bg-accent/10 border-b border-border text-center">
              <p className="text-xs text-muted-foreground">{format(currentDate, 'EEEE', { locale: it })}</p>
              <p className={`text-lg font-bold ${isToday(currentDate) ? 'text-accent' : ''}`}>{format(currentDate, 'dd MMMM', { locale: it })}</p>
            </div>
            {HOURS.map((hour) => {
              const slotBookings = getBookingsForSlot(currentDate, hour);
              const isCurrentHour = isToday(currentDate) && new Date().getHours() === hour;
              return (
                <div key={hour} ref={isCurrentHour ? currentHourRef : null}
                  className={`flex border-b border-border/50 min-h-[56px] ${isCurrentHour ? 'bg-accent/10 ring-1 ring-accent/30 ring-inset' : ''}`}>
                  <div className={`w-16 p-2 text-xs font-medium border-r border-border/50 flex items-start flex-shrink-0 ${isCurrentHour ? 'text-accent font-bold' : 'text-muted-foreground'}`}>
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  <div
                    className="flex-1 p-1 cursor-pointer hover:bg-accent/10 transition-colors"
                    onClick={() => { if (slotBookings.length === 0) openNewBookingForSlot(currentDate, hour); }}
                  >
                    {slotBookings.map((booking) => {
                      const op = operators.find(o => o.name === booking.operator_name);
                      return (
                      <div key={booking.id}
                        className={`p-2.5 rounded border mb-1 cursor-pointer hover:opacity-80 flex items-center gap-2 ${getBookingColor(booking)}`}
                        style={getOperatorStyle(booking)}
                        onClick={(e) => { e.stopPropagation(); openEditBooking(booking, e); }}
                        data-testid={`day-booking-${booking.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm flex items-center gap-1.5 flex-wrap">{booking.client_name}<NewClientBadge booking={booking} /></p>
                          <p className="opacity-80 text-xs">{booking.service_name} - {booking.time}</p>
                        </div>
                        {booking.operator_name && (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: op?.color || '#3b82f6' }}>
                            {booking.operator_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {slotBookings.length === 0 && (
                      <div className="h-full min-h-[40px] flex items-center justify-center opacity-0 hover:opacity-30 transition-opacity">
                        <Plus className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== MONTH VIEW ===== */}
      {viewMode === 'month' && (
        <div data-testid="agenda-month-view">
          <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
            {/* Day names header */}
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d) => (
              <div key={d} className="bg-secondary p-2 text-center text-xs font-semibold text-muted-foreground">{d}</div>
            ))}
            {/* Empty cells before month start */}
            {Array.from({ length: startDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="bg-card p-2 min-h-[70px]" />
            ))}
            {/* Month days */}
            {monthDays.map((day) => {
              const dayBookings = getBookingsForDate(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`bg-card p-2 min-h-[70px] cursor-pointer hover:bg-secondary/50 transition-colors ${
                    isToday(day) ? 'ring-1 ring-accent ring-inset' : ''
                  }`}
                  onClick={() => { setCurrentDate(day); setViewMode('day'); }}
                >
                  <p className={`text-sm font-medium mb-1 ${isToday(day) ? 'text-accent font-bold' : ''}`}>
                    {format(day, 'd')}
                  </p>
                  {dayBookings.length > 0 && (
                    <div className="space-y-0.5">
                      {dayBookings.slice(0, 2).map((b) => {
                        const op = operators.find(o => o.name === b.operator_name);
                        return (
                        <div key={b.id} className={`text-[10px] px-1 py-0.5 rounded truncate flex items-center gap-1 ${
                          b.status === 'pending' ? 'bg-yellow-500/30 text-yellow-300' : 'bg-accent/20 text-accent'
                        }`} style={b.operator_name && op?.color ? { backgroundColor: `${op.color}20`, color: op.color } : {}}>
                          {b.operator_name && op?.color && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: op.color }} />}
                          <NewClientBadge booking={b} iconOnly />
                          <span className="truncate">{b.time} {b.client_name?.split(' ')[0]}</span>
                        </div>
                        );
                      })}
                      {dayBookings.length > 2 && (
                        <p className="text-[10px] text-muted-foreground">+{dayBookings.length - 2} altri</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== LIST VIEW ===== */}
      {viewMode === 'list' && (
        <div className="space-y-3" data-testid="agenda-list-view">
          {allActiveBookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-muted-foreground">Nessun appuntamento programmato</p>
            </div>
          ) : (
            allActiveBookings.map((booking) => {
              const op = operators.find(o => o.name === booking.operator_name);
              return (
              <Card key={booking.id} className="card-elegant cursor-pointer hover:border-accent/30 transition-colors" onClick={() => openEditBooking(booking)} data-testid={`list-booking-${booking.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {booking.operator_name && (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: op?.color || '#3b82f6' }}>
                        {booking.operator_name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-lg truncate flex items-center gap-2">{booking.client_name}<NewClientBadge booking={booking} /></h3>
                        <Badge className={
                          booking.status === 'confirmed' ? 'bg-accent/20 text-accent' : 'bg-yellow-500/20 text-yellow-500'
                        }>
                          {booking.status === 'confirmed' ? 'Confermato' : 'In Attesa'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-accent" />
                          {format(new Date(booking.date), 'EEE dd MMM yyyy', { locale: it })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-accent" />
                          {booking.time}
                        </span>
                        {booking.client_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-accent" />
                            {booking.client_phone}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm bg-secondary inline-block px-2 py-0.5 rounded">{booking.service_name}</span>
                        {booking.operator_name && <span className="text-xs text-muted-foreground">Operatore: {booking.operator_name}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })
          )}
        </div>
      )}

      {/* Clear Dialog */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pulisci Appuntamenti di Oggi</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Vuoi cancellare tutti gli appuntamenti di oggi ({format(new Date(), 'dd MMMM yyyy', { locale: it })})?
              Questa azione non puo' essere annullata.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setClearDialogOpen(false)} className="flex-1">Annulla</Button>
              <Button
                onClick={handleClearDayBookings}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="confirm-clear-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Conferma Cancellazione
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Booking Dialog */}
      <Dialog open={newBookingOpen} onOpenChange={setNewBookingOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuovo Appuntamento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBooking} className="space-y-4">
            {/* Client Search */}
            <div className="relative">
              <Label>Cerca e Seleziona Cliente</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientResults(true);
                    if (!e.target.value) {
                      setSelectedClient(null);
                      setNewBooking({ ...newBooking, client_name: '', client_phone: '', client_email: '' });
                    }
                  }}
                  onFocus={() => setShowClientResults(true)}
                  placeholder="Cerca cliente registrato..."
                  className="pl-10"
                  data-testid="client-search-input"
                />
              </div>
              {showClientResults && filteredClients.length > 0 && (
                <div className="absolute z-50 w-full mt-1 max-h-40 overflow-y-auto bg-card border border-border rounded-lg shadow-xl" data-testid="client-search-results">
                  {filteredClients.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => selectClient(c)}
                      className="w-full px-3 py-2 text-left hover:bg-secondary transition-colors text-sm flex items-center justify-between"
                      data-testid={`select-client-${c.id}`}
                    >
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      <span className="text-xs text-muted-foreground">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedClient && (
                <p className="text-xs text-accent mt-1">Cliente selezionato: {selectedClient.first_name} {selectedClient.last_name}</p>
              )}
            </div>

            {/* Manual name if no client selected */}
            {!selectedClient && (
              <div>
                <Label>Nome Cliente *</Label>
                <Input required value={newBooking.client_name} onChange={(e) => setNewBooking({ ...newBooking, client_name: e.target.value })} placeholder="Nome e Cognome" data-testid="new-booking-name" />
              </div>
            )}
            {!selectedClient && (
              <div>
                <Label>Telefono</Label>
                <Input value={newBooking.client_phone} onChange={(e) => setNewBooking({ ...newBooking, client_phone: e.target.value })} placeholder="+39 333..." data-testid="new-booking-phone" />
              </div>
            )}
            <div>
              <Label>Servizio *</Label>
              <Select value={newBooking.service_name} onValueChange={(v) => setNewBooking({ ...newBooking, service_name: v })}>
                <SelectTrigger data-testid="new-booking-service"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name} - {s.price} EUR</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input type="date" required value={newBooking.date} onChange={(e) => setNewBooking({ ...newBooking, date: e.target.value })} data-testid="new-booking-date" />
              </div>
              <div>
                <Label>Ora *</Label>
                <Input type="time" required value={newBooking.time} onChange={(e) => setNewBooking({ ...newBooking, time: e.target.value })} data-testid="new-booking-time" />
              </div>
            </div>
            <div>
              <Label>Operatore</Label>
              <Select value={newBooking.operator_name} onValueChange={(v) => setNewBooking({ ...newBooking, operator_name: v })}>
                <SelectTrigger data-testid="new-booking-operator"><SelectValue placeholder="Seleziona operatore" /></SelectTrigger>
                <SelectContent>
                  {operators.map(op => (
                    <SelectItem key={op.id} value={op.name}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: op.color || '#3b82f6' }} />
                        {op.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setNewBookingOpen(false)} className="flex-1">Annulla</Button>
              <Button type="submit" className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="submit-new-booking">Crea Appuntamento</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog */}
      <Dialog open={!!editBooking} onOpenChange={(open) => { if (!open) setEditBooking(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Prenotazione</DialogTitle></DialogHeader>
          {editBooking && (
            <div className="space-y-3">
              <NewClientBadge booking={editBooking} />
              <PreferencesList preferences={editBooking.client_preferences} testId="agenda-booking-preferences" />
              <ReferencePhotos booking={editBooking} headers={getAuthHeaders()} />
              <div><Label>Nome Cliente</Label><Input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} className="text-base" /></div>
              <div><Label>Telefono</Label><Input value={editForm.client_phone} onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })} className="text-base" /></div>
              <div>
                <Label>Servizio</Label>
                <Select value={editForm.service_name} onValueChange={(v) => setEditForm({ ...editForm, service_name: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{services.map((s) => (<SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="text-base" /></div>
                <div><Label>Ora</Label><Input type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className="text-base" /></div>
              </div>
              <div>
                <Label>Operatore</Label>
                <Select value={editForm.operator_name || ''} onValueChange={(v) => setEditForm({ ...editForm, operator_name: v })}>
                  <SelectTrigger data-testid="edit-booking-operator"><SelectValue placeholder="Nessun operatore" /></SelectTrigger>
                  <SelectContent>
                    {operators.map(op => (
                      <SelectItem key={op.id} value={op.name}>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: op.color || '#3b82f6' }} />
                          {op.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">In Attesa</SelectItem>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Note</Label><Input value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Note..." className="text-base" /></div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditBooking(null)} className="flex-1">Annulla</Button>
                <Button onClick={handleSaveEditBooking} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-edit-agenda-booking">Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAgendaSection;
